import React, { useState, useEffect } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Flex,
  Input,
  Space,
  Tag,
  Typography,
} from 'antd';
import { SearchOutlined, CopyOutlined, CheckOutlined } from '@ant-design/icons';
import api from '../api';
import MagicLinkConfirmationModal from './MagicLinkConfirmationModal';
import './workspaceInformation/wiPhase1.css';

const { Title, Text, Paragraph } = Typography;

const USER_SEARCH_HISTORY_KEY = 'userSearchHistory';
const MAX_SEARCH_HISTORY = 10;

function statusTagColor(status) {
  if (status === 'active') return 'success';
  if (status === 'canceled' || status === 'cancelled') return 'error';
  return 'default';
}

function EnvBlock({
  label,
  color,
  envKey,
  envData,
  email,
  ssoStates,
  generateSSO,
  copySSOToClipboard,
  onViewWorkspace,
}) {
  if (!envData) return null;
  const key = `${email}-${envKey}`;
  const ssoState = ssoStates[key] || {};

  return (
    <Card
      size="small"
      title={
        <Space>
          <Tag color={color}>{label}</Tag>
          <Text type="secondary">Environment</Text>
        </Space>
      }
      style={{ marginTop: 12 }}
    >
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="Name">{envData.name || '—'}</Descriptions.Item>
        <Descriptions.Item label="Auth User ID">
          <Text code copyable={!!envData.authUserId}>
            {envData.authUserId || '—'}
          </Text>
        </Descriptions.Item>
      </Descriptions>

      <div style={{ marginTop: 16 }}>
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {!ssoState.loginUrl ? (
            <Button
              type="primary"
              onClick={() => generateSSO(email, envKey)}
              loading={!!ssoState.loading}
            >
              Generate SSO Link
            </Button>
          ) : (
            <>
              <Alert type="success" showIcon message="SSO link generated" />
              <Input.TextArea
                value={ssoState.loginUrl}
                readOnly
                autoSize={{ minRows: 2, maxRows: 4 }}
                style={{ fontFamily: 'monospace', fontSize: 12 }}
              />
              <Button
                icon={ssoState.copied ? <CheckOutlined /> : <CopyOutlined />}
                type={ssoState.copied ? 'primary' : 'default'}
                onClick={() => copySSOToClipboard(ssoState.loginUrl, key)}
              >
                {ssoState.copied ? 'Copied' : 'Copy Link'}
              </Button>
            </>
          )}
          {ssoState.error ? (
            <Alert type="error" showIcon message={ssoState.error} />
          ) : null}
        </Space>
      </div>

      {envData.accounts && envData.accounts.length > 0 ? (
        <div style={{ marginTop: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Workspaces
          </Text>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {envData.accounts.map((account, index) => (
              <Card
                key={`${envKey}-${account.authAccountId || account.accountId || index}`}
                size="small"
                type="inner"
              >
                <Flex justify="space-between" align="flex-start" gap={12} wrap="wrap">
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <Text strong>{account.slug}</Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>
                        {envKey === 'production'
                          ? `Auth Account ID: ${account.authAccountId}`
                          : `Account ID: ${account.accountId}`}
                      </Text>
                    </div>
                    <Space size="middle" wrap style={{ marginTop: 6 }}>
                      <span>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Status:{' '}
                        </Text>
                        <Tag color={statusTagColor(account.subscriptionStatus)}>
                          {account.subscriptionStatus || 'N/A'}
                        </Tag>
                      </span>
                      <span>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Plan:{' '}
                        </Text>
                        <Tag>{account.subscriptionPlan || 'N/A'}</Tag>
                      </span>
                    </Space>
                  </div>
                  <Button type="primary" ghost onClick={() => onViewWorkspace(account.slug, envKey)}>
                    View Workspace
                  </Button>
                </Flex>
              </Card>
            ))}
          </Space>
        </div>
      ) : null}
    </Card>
  );
}

const UserSSOLogin = ({ onNavigateToWorkspace }) => {
  const [email, setEmail] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ssoStates, setSsoStates] = useState({});
  const [magicLinkStates, setMagicLinkStates] = useState({});
  const [showMagicLinkModal, setShowMagicLinkModal] = useState(false);
  const [magicLinkModalEmail, setMagicLinkModalEmail] = useState('');
  const [magicLinkModalEnvironment, setMagicLinkModalEnvironment] = useState('');
  const [searchHistory, setSearchHistory] = useState([]);

  const searchByEmail = async (emailToSearch) => {
    if (!emailToSearch || !emailToSearch.trim()) {
      setError('Please enter an email address');
      return;
    }

    setLoading(true);
    setError('');
    setUserInfo(null);
    setSsoStates({});
    setMagicLinkStates({});
    setShowMagicLinkModal(false);

    try {
      const response = await api.get(
        `/api/workspace/user-info?email=${encodeURIComponent(emailToSearch)}`
      );
      setUserInfo(response.data.data);
      const trimmedEmail = emailToSearch.trim().toLowerCase();
      const historyFromStorage = localStorage.getItem(USER_SEARCH_HISTORY_KEY);
      const currentHistory = historyFromStorage ? JSON.parse(historyFromStorage) : [];
      const historyEntry = { value: trimmedEmail, timestamp: Date.now() };
      const newHistory = [
        historyEntry,
        ...currentHistory.filter((h) => h.value !== trimmedEmail),
      ].slice(0, MAX_SEARCH_HISTORY);
      setSearchHistory(newHistory);
      localStorage.setItem(USER_SEARCH_HISTORY_KEY, JSON.stringify(newHistory));
    } catch (err) {
      console.error('Error fetching user info:', err);
      if (err.response?.status === 404) {
        setError('User not found in any environment');
      } else {
        setError(err.response?.data?.error || 'Failed to fetch user information');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    searchByEmail(email);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  useEffect(() => {
    const history = localStorage.getItem(USER_SEARCH_HISTORY_KEY);
    if (history) {
      try {
        setSearchHistory(JSON.parse(history));
      } catch (e) {
        console.error('Error parsing user search history:', e);
      }
    }
  }, []);

  const generateSSO = async (userEmail, environment) => {
    const key = `${userEmail}-${environment}`;
    setSsoStates((prev) => ({ ...prev, [key]: { loading: true, error: '', loginUrl: '' } }));

    try {
      const response = await api.post('/api/workspace/user-sso', {
        email: userEmail,
        environment: environment,
      });

      setSsoStates((prev) => ({
        ...prev,
        [key]: {
          loading: false,
          error: '',
          loginUrl: response.data.data.loginUrl,
        },
      }));
    } catch (err) {
      console.error('Error generating SSO login:', err);
      setSsoStates((prev) => ({
        ...prev,
        [key]: {
          loading: false,
          error: err.response?.data?.error || 'Failed to generate login link',
          loginUrl: '',
        },
      }));
    }
  };

  const copySSOToClipboard = async (loginUrl, key) => {
    try {
      await navigator.clipboard.writeText(loginUrl);
      setSsoStates((prev) => ({
        ...prev,
        [key]: { ...prev[key], copied: true },
      }));
      setTimeout(() => {
        setSsoStates((prev) => ({
          ...prev,
          [key]: { ...prev[key], copied: false },
        }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleMagicLinkConfirm = async () => {
    setShowMagicLinkModal(false);
    const userEmail = magicLinkModalEmail;
    const environment = magicLinkModalEnvironment;
    const key = `${userEmail}-${environment}`;

    setMagicLinkStates((prev) => ({
      ...prev,
      [key]: { loading: true, error: '', success: false },
    }));

    try {
      await api.post('/api/workspace/send-magic-link', {
        email: userEmail,
        environment: environment,
      });

      setMagicLinkStates((prev) => ({
        ...prev,
        [key]: {
          loading: false,
          error: '',
          success: true,
        },
      }));
    } catch (err) {
      console.error('Error sending magic link:', err);
      setMagicLinkStates((prev) => ({
        ...prev,
        [key]: {
          loading: false,
          error: err.response?.data?.error || 'Failed to send magic link',
          success: false,
        },
      }));
    } finally {
      setMagicLinkModalEmail('');
      setMagicLinkModalEnvironment('');
    }
  };

  const handleMagicLinkCancel = () => {
    setShowMagicLinkModal(false);
    setMagicLinkModalEmail('');
    setMagicLinkModalEnvironment('');
  };

  const handleNavigateToWorkspace = (slug, environment) => {
    if (onNavigateToWorkspace) {
      onNavigateToWorkspace('workspace-info');
      setTimeout(() => {
        sessionStorage.setItem(
          'workspaceSearch',
          JSON.stringify({
            searchType: 'slug',
            searchValue: slug,
            environment: environment,
          })
        );
        window.dispatchEvent(new Event('workspaceSearchRequested'));
      }, 100);
    }
  };

  return (
    <div className="wi-page">
      <div className="wi-panel">
        <Card bordered={false} className="wi-search-card" styles={{ body: { padding: 24 } }}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Title level={3} style={{ margin: 0 }}>
                User Information / SSO
              </Title>
              <Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 4 }}>
                Search for user details by email and generate SSO login links
              </Paragraph>
            </div>

            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                User Email
              </Text>
              <div className="wi-search-row">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  onPressEnter={handleKeyPress}
                  placeholder="Enter user email (e.g., user@example.com)"
                  allowClear
                  size="large"
                  style={{ flex: 1 }}
                />
                <Button
                  type="primary"
                  size="large"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  loading={loading}
                >
                  Search
                </Button>
              </div>
            </div>

            {searchHistory.length > 0 && (
              <Flex wrap="wrap" gap={8} align="center">
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Recent searches
                </Text>
                {searchHistory.map((h, i) => (
                  <Tag
                    key={`${h.value}-${i}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setEmail(h.value);
                      setError('');
                      searchByEmail(h.value);
                    }}
                  >
                    {h.value}
                  </Tag>
                ))}
                <Button
                  type="link"
                  size="small"
                  danger
                  onClick={() => {
                    setSearchHistory([]);
                    localStorage.removeItem(USER_SEARCH_HISTORY_KEY);
                  }}
                >
                  Clear
                </Button>
              </Flex>
            )}
          </Space>
        </Card>

        {error ? (
          <Alert type="error" showIcon message={error} style={{ marginTop: 16 }} />
        ) : null}

        {userInfo ? (
          <Card title="User Details" style={{ marginTop: 16 }} className="wi-details-card">
            <Descriptions column={1} bordered size="middle">
              <Descriptions.Item label="Email">
                <Text code copyable>
                  {userInfo.email}
                </Text>
              </Descriptions.Item>
            </Descriptions>

            <EnvBlock
              label="Production"
              color="success"
              envKey="production"
              envData={userInfo.production}
              email={userInfo.email}
              ssoStates={ssoStates}
              generateSSO={generateSSO}
              copySSOToClipboard={copySSOToClipboard}
              onViewWorkspace={handleNavigateToWorkspace}
            />

            <EnvBlock
              label="Staging"
              color="warning"
              envKey="staging"
              envData={userInfo.staging}
              email={userInfo.email}
              ssoStates={ssoStates}
              generateSSO={generateSSO}
              copySSOToClipboard={copySSOToClipboard}
              onViewWorkspace={handleNavigateToWorkspace}
            />
          </Card>
        ) : null}

        {!userInfo && !error && !loading ? (
          <Empty
            description="Enter an email address to view user information"
            style={{ margin: '48px 0' }}
          />
        ) : null}
      </div>

      <MagicLinkConfirmationModal
        isOpen={showMagicLinkModal}
        email={magicLinkModalEmail}
        onConfirm={handleMagicLinkConfirm}
        onCancel={handleMagicLinkCancel}
      />
    </div>
  );
};

export default UserSSOLogin;
